import React, { useEffect, useMemo } from "react";
import { useStat } from "../../../DataStore/StatContext";
import { numberFormat } from "../../../util/araknerdlib";
import { useCurrency } from "../../../util/SystemVariables";
import { Skeleton } from "../../../util/skeleton/Skeleton";
import { Link } from "react-router-dom";
import FinanceCardHead from "../FinanceCardHead";

const InvoicesCard = () => {
  const { creditorInvoicing, getCreditorInvoicing, loadingCreditorInvoicing } = useStat();
  const currency = useCurrency();

  useEffect(() => {
    getCreditorInvoicing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const ci = creditorInvoicing || {};
    const outstanding = Number(ci.outstanding_sum || 0);
    const overdue = Number(ci.over_due_sum || 0);
    const nonOverdue = Math.max(0, outstanding - overdue);
    const invoiceSum = Number(ci.invoice_sum || 0);

    let paid30 = 0;
    if (ci.last_payment?.payment) {
      const p = Number(ci.last_payment.payment || 0);
      const dt =
        ci.last_payment.transaction_date ||
        (ci.last_payment.created_at ? String(ci.last_payment.created_at).slice(0, 10) : null);

      if (dt) {
        const now = new Date();
        const when = new Date(dt);
        const diffDays = Math.floor((now - when) / (1000 * 60 * 60 * 24));
        if (diffDays <= 30 && diffDays >= 0) paid30 = p;
      }
    }

    return {
      unpaidTotal: outstanding,
      overdueTotal: overdue,
      nonOverdueTotal: nonOverdue,
      paidLast30: paid30,
      invoiceSumPeriod: invoiceSum,
    };
  }, [creditorInvoicing]);

  const isLoading = loadingCreditorInvoicing || !creditorInvoicing;

  if (isLoading) {
    return (
      <div className="yx-card mb-3">
        <div className="yx-card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Skeleton w="180px" h={14} />
            <Skeleton w="120px" h={12} />
          </div>

          <Skeleton w="260px" h={30} />
          <Skeleton w="160px" h={12} style={{ marginTop: 10 }} />

          <div className="yx-divider" />

          <div className="d-flex justify-content-between mb-2">
            <Skeleton w="210px" h={12} />
            <Skeleton w="130px" h={14} />
          </div>
          <div className="d-flex justify-content-between mb-2">
            <Skeleton w="110px" h={12} />
            <Skeleton w="110px" h={14} />
          </div>
          <div className="d-flex justify-content-between mb-2">
            <Skeleton w="130px" h={12} />
            <Skeleton w="130px" h={14} />
          </div>

          <div className="yx-divider" />

          <div className="d-flex justify-content-between mb-2">
            <Skeleton w="150px" h={12} />
            <Skeleton w="130px" h={16} />
          </div>

          <Skeleton w="130px" h={12} style={{ marginTop: 14 }} />
        </div>
      </div>
    );
  }

  const Row = ({ label, value, danger = false }) => (
    <div className="d-flex justify-content-between align-items-center" style={{ gap: 14 }}>
      <div className="yx-muted" style={{ fontWeight: 800, fontSize: 13 }}>
        {label}
      </div>
      <div className={`yx-row ${danger ? "is-negative" : ""}`} style={{ whiteSpace: "nowrap" }}>
        {value}
      </div>
    </div>
  );

  return (
    <div className="yx-card mb-3">
      <FinanceCardHead
        title="Creditors invoices"
        right={
          <span className="yx-pill yx-pill--muted">
            Current period <i className="fa fa-chevron-down ml-1" />
          </span>
        }
        kicker="Creditors invoices"
        value={`${currency} ${numberFormat(stats.unpaidTotal)}`}
        sub="Outstanding invoices"
      >
        <div className="yx-card-body">
          <div className="yx-divider" />

          <div className="d-flex flex-column" style={{ gap: 12 }}>
            <Row
              label="Total invoiced (this period)"
              value={`${currency} ${numberFormat(stats.invoiceSumPeriod)}`}
            />
            <Row
              label="Overdue"
              value={`${currency} ${numberFormat(stats.overdueTotal)}`}
              danger={stats.overdueTotal > 0}
            />
            <Row
              label="Non overdue"
              value={`${currency} ${numberFormat(stats.nonOverdueTotal)}`}
            />
          </div>

          <div className="yx-divider" />

          <Row
            label="Paid last 30 days"
            value={`${currency} ${numberFormat(stats.paidLast30)}`}
          />

          <Link to="/expenses-invoicing" className="yx-link d-inline-flex align-items-center mt-3">
            See all invoices <i className="fa fa-arrow-right ml-2" />
          </Link>
        </div>
      </FinanceCardHead>
    </div>
  );
};

export default InvoicesCard;
